// Página AdminSales - Gestión de ventas (listado)
// Muestra tabla de ventas con filtros y acciones

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSales } from '../../hooks/useSales'
import { useSaleOperations } from '../../hooks/useSales'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import ConfirmDialog from '../../components/ConfirmDialog'
import { formatDate, formatCurrency } from '../../utils/helpers'
import { notifyPaymentApproved } from '../../services/notifications.service'
import './AdminSales.css'

/**
 * Página AdminSales
 * Listado y gestión de ventas
 */
function AdminSales() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all') // all, completed, cancelled
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [notification, setNotification] = useState(null)

  // Construir filtros
  const filters = {}
  if (filterStatus !== 'all') filters.status = filterStatus
  if (filterPaymentMethod !== 'all') filters.paymentMethod = filterPaymentMethod
  if (startDate) filters.startDate = startDate
  if (endDate) filters.endDate = endDate

  // Obtener ventas
  const { sales, loading, error, refetch } = useSales(filters)
  const { delete: deleteSale, update: updateSale, loading: operationLoading } = useSaleOperations()

  // Filtrar ventas por búsqueda
  const filteredSales = sales.filter(sale => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      (sale.saleNumber || '').toLowerCase().includes(query) ||
      (sale.orderNumber || '').toLowerCase().includes(query) ||
      (sale.customer?.name || '').toLowerCase().includes(query) ||
      (sale.customer?.email || '').toLowerCase().includes(query)
    )
  })

  // Calcular estadísticas
  const stats = {
    total: filteredSales.length,
    totalAmount: filteredSales.reduce((sum, sale) => sum + (sale.total || 0), 0),
    completed: filteredSales.filter(s => s.status === 'completed').length,
    cancelled: filteredSales.filter(s => s.status === 'cancelled').length,
    paid: filteredSales.filter(s => s.paid === true).length
  }

  // Manejar eliminación de venta
  const handleDelete = async (saleId) => {
    try {
      await deleteSale(saleId)
      setDeleteConfirm(null)
      setNotification({
        type: 'success',
        message: 'Venta eliminada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al eliminar la venta'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const handleQuickPay = async (sale) => {
    try {
      await updateSale(sale.id, {
        paid: true
      })
      if (sale.orderId) {
        notifyPaymentApproved(sale.orderId).catch(() => null)
      }
      setNotification({
        type: 'success',
        message: `Venta ${sale.saleNumber || ''} marcada como cobrada`
      })
      setTimeout(() => setNotification(null), 3000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al marcar la venta como cobrada'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const handleQuickDeliver = async (sale) => {
    try {
      await updateSale(sale.id, {
        status: 'completed'
      })
      setNotification({
        type: 'success',
        message: `Venta ${sale.saleNumber || ''} marcada como entregada`
      })
      setTimeout(() => setNotification(null), 3000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al marcar la venta como entregada'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  if (loading) {
    return (
      <div className="admin-sales-page">
        <div className="loading-state">
          <p>Cargando ventas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-sales-page">
      <div className="page-header">
        <div>
          <h1>Gestión de Ventas</h1>
          <p>Administra las ventas registradas</p>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Ventas</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats.totalAmount)}</div>
          <div className="stat-label">Monto Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.completed}</div>
          <div className="stat-label">Completadas</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.cancelled}</div>
          <div className="stat-label">Canceladas</div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Buscar por número, pedido o cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="pending_payment">Pendiente Pago</option>
            <option value="pending_delivery">Pendiente Entrega</option>
            <option value="completed">Completadas</option>
            <option value="cancelled">Canceladas</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            value={filterPaymentMethod}
            onChange={(e) => setFilterPaymentMethod(e.target.value)}
            className="filter-select"
          >
            <option value="all">Todos los métodos</option>
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia</option>
            <option value="tarjeta">Tarjeta</option>
            <option value="cheque">Cheque</option>
          </select>
        </div>
        <div className="filter-group">
          <input
            type="date"
            placeholder="Fecha inicio"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <input
            type="date"
            placeholder="Fecha fin"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="filter-input"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-state">
          <p>Error: {error}</p>
          <Button onClick={refetch}>Reintentar</Button>
        </div>
      )}

      {/* Listado de ventas */}
      {!loading && !error && (
        <>
          <div className="sales-count">
            Mostrando {filteredSales.length} de {filteredSales.length} venta(s)
          </div>

          {filteredSales.length > 0 ? (
            <>
              <div className="sales-cards-wrapper">
                <div className="sales-cards-container">
                {filteredSales.map((sale) => (
                  <div key={sale.id} className={`sale-card ${sale.status === 'cancelled' ? 'cancelled' : ''}`}>
                    <div className="sale-card-header">
                      <div>
                        <strong className="sale-card-number">{sale.saleNumber || '-'}</strong>
                        <Link to={`/admin/pedidos/${sale.orderId}`} className="sale-order-link">
                          Pedido: {sale.orderNumber || '-'}
                        </Link>
                      </div>
                      <span className={`status-badge ${
                        sale.status === 'completed' ? 'completed' : 
                        sale.status === 'cancelled' ? 'cancelled' :
                        sale.status === 'pending_payment' ? 'pending-payment' :
                        sale.status === 'pending_delivery' ? 'pending-delivery' :
                        'pending'
                      }`}>
                        {sale.status === 'completed' ? '✓ Completada' : 
                         sale.status === 'cancelled' ? '✕ Cancelada' :
                         sale.status === 'pending_payment' ? '⏳ Pendiente Pago' :
                         sale.status === 'pending_delivery' ? '📦 Pendiente Entrega' :
                         '⏱️ Pendiente'}
                      </span>
                    </div>
                    <div className="sale-card-body">
                      <div className="sale-card-info-row">
                        <span className="info-label">Cliente:</span>
                        <div className="info-value-group">
                          <span className="info-value">{sale.customer?.name || '-'}</span>
                          {sale.customer?.email && (
                            <small className="customer-email">{sale.customer.email}</small>
                          )}
                        </div>
                      </div>
                      <div className="sale-card-info-row">
                        <span className="info-label">Fecha:</span>
                        <span className="info-value">{formatDate(sale.saleDate)}</span>
                      </div>
                      <div className="sale-card-info-row">
                        <span className="info-label">Método de Pago:</span>
                        <span className="payment-method-badge">
                          {sale.paymentMethod === 'efectivo' ? '💵' : 
                           sale.paymentMethod === 'transferencia' ? '🏦' :
                           sale.paymentMethod === 'tarjeta' ? '💳' :
                           sale.paymentMethod === 'cheque' ? '📝' : '💰'}
                          {sale.paymentMethod || '-'}
                        </span>
                      </div>
                      <div className="sale-card-info-row">
                        <span className="info-label">Estado de Pago:</span>
                        {sale.paid ? (
                          <span style={{ fontSize: '0.85rem', color: '#28a745', fontWeight: 600 }}>
                            💵 Cobrada
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.85rem', color: '#dc3545', fontWeight: 600 }}>
                            ⏳ Sin Cobrar
                          </span>
                        )}
                      </div>
                      <div className="sale-card-info-row total-row">
                        <span className="info-label">Total:</span>
                        <strong className="total-amount">{formatCurrency(sale.total || 0)}</strong>
                      </div>
                    </div>
                    <div className="sale-card-actions">
                      <Link to={`/admin/ventas/${sale.id}`}>
                        <Button variant="outline" size="small" style={{ width: '100%' }}>
                          👁️ Ver Detalle
                        </Button>
                      </Link>
                      {!sale.paid && (
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => handleQuickPay(sale)}
                          disabled={operationLoading}
                          style={{ width: '100%' }}
                        >
                          💵 Cobrar
                        </Button>
                      )}
                      {sale.status !== 'completed' && sale.status !== 'cancelled' && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => handleQuickDeliver(sale)}
                          disabled={operationLoading}
                          style={{ width: '100%' }}
                        >
                          📦 Entregar
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="small"
                        onClick={() => setDeleteConfirm(sale.id)}
                        disabled={operationLoading}
                        style={{ width: '100%' }}
                      >
                        🗑️ Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>No hay ventas disponibles</p>
            </div>
          )}
        </>
      )}

      {/* Diálogo de confirmación */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
        title="Eliminar Venta"
        message="¿Estás seguro de eliminar esta venta? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
      />

      {/* Notificaciones */}
      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default AdminSales

